import { useForm } from "react-hook-form";
import { useState, useEffect, useMemo } from "react";
import Modal from "./Modal";
import { useDispatch, useSelector } from "react-redux";
import {
  obtenerNivelesAcademicosAdm,
  cambiarOrdenNivelAcademico,
  cambiarEstadoNivelAcademico,
} from "../../redux/carrerasSlice";
import { toast } from "react-toastify";
import WindowDimension from "../../Hooks/WindowDimension";
import OperationStatus from "../../config/OperationStatus";
import ModalRemove from "./ModalRemove";
import Required from "../../Components/Required";
import { OrderTable } from "../../Components/OrderTable";
import { getAdminAcademicPage } from "../../Api/content";
import { useLocale } from "../../i18n/localeContext";
import { Skeleton } from "antd";
import { _translateValue } from "../translation-gestor/translateValue";

import { useValidation } from "../../Hooks/useValidation";

const SCREEN_NAME = "PantallaNivelesAcademicos";
function NivelesAcademicos(props) {
  const { validation } = useValidation();
  const { locale } = useLocale();
  const [adminAcademicPage, setAdminAcademicPage] = useState(null);
  const [isLoadingContent, setIsLoadingContent] = useState(true);
  const [translatedNombres, setTranslatedNombres] = useState({});
  const [isTranslating, setIsTranslating] = useState(false);
  const [removeState, changeRemoveState] = useState(false);
  const [registerSelected = [], selectReg] = useState();
  const [showModal, setShowModal] = useState(false);
  const { width } = WindowDimension();

  const dispatch = useDispatch();

  const { nivelesAcademicos } = useSelector((state) => state.carreras);

  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm();

  const columns = useMemo(() => {
    const academyLevelTable = adminAcademicPage?.academyLevel_table ?? [];
    return [
      { label: academyLevelTable[0]?.label || 'Código', accessor: 'nivelId', sortable: true },
      { 
        label: academyLevelTable[1]?.label || 'Nombre', 
        accessor: 'nivelAcademicoNombre', 
        sortable: true,
        render: (item) => (
          <td>{translatedNombres[item.nivelAcademicoNombre] || item.nivelAcademicoNombre}</td>
        ),
      },
      { label: academyLevelTable[2]?.label || 'Orden', accessor: 'orden', sortable: true },
      {
        label: academyLevelTable[3]?.label || 'Estado',
        accessor: 'estadoNombre',
        sortable: true,
        render: (item) => (
          <td>
            <span className={item.estadoId == 4 ? "success-d" : "danger-d"}>
              {item.estadoNombre}
            </span>
          </td>
        ),
      },
      { label: academyLevelTable[4]?.label || 'Acción', accessor: 'action', sortable: false },
    ];
  }, [adminAcademicPage, translatedNombres]);

  const showModalEdit = (item) => {
    reset(item);
    setShowModal(true);
  };

  const closeModalEdit = () => {
    setShowModal(false);
  };

  const cargarNiveles = () => {
    dispatch(obtenerNivelesAcademicosAdm());
  };

  const onSubmit = async (data) => {
    let { payload } = await dispatch(cambiarOrdenNivelAcademico(data));

    if (payload.operationStatus === OperationStatus.SUCCESS) {
      closeModalEdit();
      cargarNiveles();
      toast.success(payload.message);
    } else {
      toast.error(payload.message);
    }
  };

  const activacion = async (estado, estatus) => {
    let { payload } = await dispatch(
      cambiarEstadoNivelAcademico({
        id: registerSelected.nivelId,
        estadoId: estado,
      })
    );
    if (payload.operationStatus === OperationStatus.SUCCESS) {
      toast.success(payload.message);
      cargarNiveles();
      changeRemoveState(false);
    }
  };

  useEffect(() => {
    cargarNiveles();
  }, [dispatch]);

  useEffect(() => {
    let cancel = false;
    setIsLoadingContent(true);
    getAdminAcademicPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          setAdminAcademicPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => { 
        if (!cancel) {
          setAdminAcademicPage(null);
          setIsLoadingContent(false);
        }
      });
    return () => { cancel = true };
  }, [locale])

  // Efecto para traducir el campo "Nombre" cuando lleguen los datos o cambie el locale
  useEffect(() => {
    const translateData = async () => {
      // Verificar si hay datos para traducir
      const hasNiveles = nivelesAcademicos && Array.isArray(nivelesAcademicos) && nivelesAcademicos.length > 0;

      // Si no hay datos, no hacer nada
      if (!hasNiveles) {
        setIsTranslating(false);
        return;
      }

      // Iniciar traducción
      setIsTranslating(true);

      const nombreTranslations = {};

      // Traducir solo el campo "Nombre"
      const translationPromises = nivelesAcademicos.map(async (item) => {
        if (item.nivelAcademicoNombre) {
          const translated = await _translateValue(SCREEN_NAME, item.nivelAcademicoNombre, locale);
          nombreTranslations[item.nivelAcademicoNombre] = translated;
        }
      });
      await Promise.all(translationPromises);

      // Actualizar estados y finalizar traducción
      setTranslatedNombres(nombreTranslations);
      setIsTranslating(false);
    };

    translateData();
  }, [nivelesAcademicos, locale]);

  if (isLoadingContent || isTranslating) {
    return (
      <div className="main-admin">
        <div className="main-admin-bar">
          <div className="header-admin-bar-items">
            <Skeleton.Input style={{ width: 300, height: 30 }} active />
          </div>
        </div>
        <div className="table-container">
          <Skeleton.Input style={{ width: '100%', height: 400, minWidth: '800px' }} active />
        </div>
      </div>
    );
  }

  return (
    <div className="main-admin">
      <div className="main-admin-bar">
        <div className="header-admin-bar-items">
          <h2>{adminAcademicPage?.title || "Niveles académicos"}</h2>
        </div>
      </div>

      <div className="table-container">
        {nivelesAcademicos.length > 0 ? (
          <>
            <OrderTable
              {...{ columns }}
              data={nivelesAcademicos}
              clase="table-adm"
              renderActionItems={(item) => {
                return (
                  <td key={Math.random()}>
                    <br />
                    <button
                      className="modBtn text-primary "
                      onClick={() => {
                        showModalEdit(item);
                      }}
                    >
                      <i className="material-icons"></i>
                    </button>

                    {item.estadoId === 4 ? (
                      <button className="modBtn text-primary">
                        <i
                          className="material-icons statusRed"
                          onClick={() => {
                            selectReg(item);
                            changeRemoveState(true);
                          }}
                        >
                          
                        </i>
                      </button>
                    ) : (
                      <button className="modBtn text-primary ">
                        <i
                          className="material-icons statusGreen"
                          onClick={() => {
                            selectReg(item);
                            changeRemoveState(true);
                          }}
                        >
                          
                        </i>
                      </button>
                    )}
                  </td>
                );
              }}
            />
          </>
        ) : (
          <div className="no-results-container">
            <img src={props.img} alt="Imagen sin resultados" />
            <h1>{props.noResultMessage}</h1>
          </div>
        )}
      </div>

      <Modal
        estado={showModal}
        cambiarEstado={closeModalEdit}
        titulo={(adminAcademicPage?.subtitle || "Editar")}
        clase={`${width < 576 ? "full-width" : "small"}`}
        buttonGroup={
          <>
            <button
              type="submit"
              className="button-text"
              onClick={handleSubmit(onSubmit)}
            >
              {adminAcademicPage?.save_button || "Guardar"}
            </button>
          </>
        }
      >
        <form>
          <div className="form-group">
            <label>{adminAcademicPage?.order || "Orden"}</label>
            <Required />
            <input
              className="form-control"
              type="number"
              {...register("orden", { required: validation?.required_field || "Campo requerido" })}
            />
            {errors.orden && <p className="error">{errors.orden.message}</p>}
          </div>
        </form>
      </Modal>

      <ModalRemove
        estado={removeState}
        closeModalHandled={changeRemoveState}
        title={
          registerSelected.estadoId === 4
            ? adminAcademicPage?.subtitleTR || "Desactivar nivel académico"
            : adminAcademicPage?.subtitleT || "Activar nivel académico"
        }
        message={
          registerSelected.estadoId === 4
            ? adminAcademicPage?.questionA || "¿Desea desactivar el siguiente nivel académico?"
            : adminAcademicPage?.question || "¿Desea activar el siguiente nivel académico?"
        }
        item={registerSelected.carreraNombre}
        icon={
          registerSelected.estadoId === 5 ? (
            <i className="material-icons info"></i>
          ) : null
        }
        color={registerSelected.estadoId === 5 ? "activate-item" : null}
        btnGroup={
          <>
            <button
              className="button-text text-red"
              onClick={() =>
                activacion(registerSelected.estadoId === 4 ? 5 : 4)
              }
            >
              {adminAcademicPage?.confirm_button || "Confirmar"}
            </button>
            <button
              className="button-text text-grey"
              onClick={() => {
                changeRemoveState(false);
              }}
            >
              {adminAcademicPage?.cancel_button || "Cancelar"}
            </button>
          </>
        }
      />
    </div>
  );
}

export default NivelesAcademicos;
